import { plural } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { Activity, Eye, EyeOff, X } from 'lucide-react'
import React from 'react'
import styled from 'styled-components'
import { MOBILE_LAYOUT_QUERY } from 'utils/mobileLayout'

import type { ActiveModel } from './modelCard'
import { ModelPicker } from './ModelPicker'
import type { AgentModel } from './useAgentModels'

const Bar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.5rem;
  flex-shrink: 0;
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};

  @media ${MOBILE_LAYOUT_QUERY} {
    flex-wrap: wrap;
  }
`

const Trailing = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
`

const Chip = styled(Text)<{ $warn?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border: 1px solid
    ${({ $warn, theme }) =>
      $warn ? theme.forecast.dangerText : theme.forecast.warning};
  color: ${({ $warn, theme }) =>
    $warn ? theme.forecast.dangerText : theme.forecast.warning};
  border-radius: 999px;
  padding: 0.0625rem 0.4375rem;
  white-space: nowrap;
`

/**
 * One bar for everything model-related: which model is on the chart, and how it
 * is drawn. Keeps the sidebar's vertical space for the conversation; everything
 * ABOUT a model lives in the card in the bottom dock.
 */
export const AgentHeader: React.FC<{
  models: AgentModel[]
  active: ActiveModel | null
  onView: (model: AgentModel) => void
  onOpenDetail: (model: AgentModel) => void
  /** Opens the model card for whatever is on the chart. */
  onOpenCard: () => void
  onDelete: (model: AgentModel) => void
  onCloseView: () => void
  /** Whether the chart is showing only the KPI and its expectation. */
  kpiOnly: boolean
  onKpiOnlyChange: (kpiOnly: boolean) => void
  /** How many tag pens the toggle hides. */
  hidablePenCount: number
  showResidual: boolean
  onShowResidualChange: (show: boolean) => void
  /** False when the model carries no saved fit series to compute from. */
  canShowResidual: boolean
  /** Controls that belong to the conversation rather than the model. */
  trailing?: React.ReactNode
}> = ({
  models,
  active,
  onView,
  onOpenDetail,
  onOpenCard,
  onDelete,
  onCloseView,
  kpiOnly,
  onKpiOnlyChange,
  hidablePenCount,
  showResidual,
  onShowResidualChange,
  canShowResidual,
  trailing,
}) => {
  const { t } = useLingui()
  const kpiIssue = active?.kpiIssue
  const missingForKpi = active?.missingForKpi?.join(', ')
  return (
    <Bar>
      <ModelPicker
        models={models}
        active={active}
        onView={onView}
        onOpenDetail={onOpenDetail}
        onDelete={onDelete}
      />
      {active && (hidablePenCount > 0 || kpiOnly) && (
        <Button
          variant='outline'
          size='xs'
          pressed='accent'
          aria-pressed={kpiOnly}
          title={
            kpiOnly
              ? plural(hidablePenCount, {
                  one: 'Show the # tag pen again',
                  other: 'Show the # tag pens again',
                })
              : plural(hidablePenCount, {
                  one: 'Hide the # tag pen, leaving the KPI and its expectation',
                  other:
                    'Hide all # tag pens, leaving the KPI and its expectation',
                })
          }
          onClick={() => onKpiOnlyChange(!kpiOnly)}>
          {kpiOnly ? (
            <Eye data-icon='inline-start' />
          ) : (
            <EyeOff data-icon='inline-start' />
          )}
          {t`KPI only`}
        </Button>
      )}
      {active && canShowResidual && (
        <Button
          variant='outline'
          size='xs'
          pressed='accent'
          aria-pressed={showResidual}
          title={
            showResidual
              ? t`Remove the residual pen`
              : t`Add the residual (actual − expected), computed from the saved fit`
          }
          onClick={() => onShowResidualChange(!showResidual)}>
          <Activity data-icon='inline-start' />
          {t`Residual`}
        </Button>
      )}
      {active?.unsaved && (
        <Chip
          as='button'
          variant='caption'
          $warn
          title={t`This model is not saved — open the model card to name and save it`}
          onClick={onOpenCard}>
          {t`not saved`}
        </Chip>
      )}
      {active && (
        <>
          {active.kpiIssue ? (
            <Chip
              variant='caption'
              $warn
              title={t`The KPI line cannot draw — ${kpiIssue}`}>
              {t`KPI not chartable`}
            </Chip>
          ) : active.missingForKpi?.length ? (
            <Chip
              variant='caption'
              $warn
              title={t`The KPI line cannot draw: this site has no pen for ${missingForKpi}`}>
              {t`KPI line unavailable`}
            </Chip>
          ) : (
            <Chip variant='caption' title={t`This model is drawn on the chart`}>
              {t`on chart`}
            </Chip>
          )}
          <Button
            variant='ghostMuted'
            size='icon-sm'
            title={t`Take this model off the chart and bring your pens back`}
            aria-label={t`Take this model off the chart`}
            onClick={onCloseView}>
            <X />
          </Button>
        </>
      )}
      {trailing && <Trailing>{trailing}</Trailing>}
    </Bar>
  )
}
